import { InputSelect, Modal } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { NeedCategoryDescriptionEnum } from '@lmc/validations/lib/schemas/v6/needs'
import { Divider, Form } from 'antd'
import { type NeedItem, useNeedUpdate } from 'hooks/useNeeds'
import { type FC, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

type EditNeedModalProps = {
  isModalVisible: boolean
  onClose: () => void
  need: NeedItem | null
}

export const EditNeedModal: FC<EditNeedModalProps> = ({
  isModalVisible,
  onClose,
  need
}) => {
  const { t } = useTranslation()
  const [form] = Form.useForm()

  const handleCloseForm = () => {
    form.resetFields()
    form.setFieldsValue({ modsCategory: undefined })
    onClose()
  }

  const { updateNeed, isUpdating } = useNeedUpdate(handleCloseForm)

  useEffect(() => {
    if (isModalVisible && need) {
      form.setFieldsValue({ modsCategory: need.modsCategory })
    }
  }, [form, isModalVisible, need?.id, need?.modsCategory])

  const categoryOptions = Object.entries(NeedCategoryDescriptionEnum).map(
    ([code, description]) => ({ label: description, value: code })
  )

  const onConfirm = () => {
    form.submit()
  }

  const onFinish = async (values: { modsCategory?: string }) => {
    if (!need) return
    await updateNeed({
      id: need.id,
      name: need.name,
      modsCategory: values.modsCategory
    })
  }

  return (
    <StyledModal
      localisation={{
        title: t('skillsAndNeeds.editNeedModalTitle'),
        confirmText: t('skillsAndNeeds.saveChanges'),
        cancelText: t('general.buttons.cancel')
      }}
      onConfirm={onConfirm}
      onCancel={handleCloseForm}
      isModalVisible={isModalVisible}
      closeButton
      closeOnConfirm={false}
      noPadding
      confirmButtonProps={{ isLoading: isUpdating, variant: 'primary' }}
    >
      <Divider style={{ margin: '0 0 16px 0' }} />
      <Form form={form} onFinish={onFinish} key={need?.id}>
        <StyledReadOnlyField>
          <StyledFieldLabel weight="bold">
            {t('skillsAndNeeds.form.needName')}
          </StyledFieldLabel>
          <StyledFieldValue>{need?.name}</StyledFieldValue>
        </StyledReadOnlyField>
        <InputSelect
          testId="edit-need-category-select"
          type="modsCategory"
          required
          label={t('skillsAndNeeds.form.needCategoryLabel')}
          helperText={t('skillsAndNeeds.form.needCategoryTooltip')}
          errorMessage={t('skillsAndNeeds.form.needCategoryRequired')}
          layout="column"
          placeholder={t('skillsAndNeeds.form.needCategoryPlaceholder')}
          options={categoryOptions}
          showSearch
        />
      </Form>
      <Divider style={{ margin: '8px 0 0 0' }} />
    </StyledModal>
  )
}

const StyledReadOnlyField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 12px;
`

const StyledFieldLabel = styled(Text)`
  font-weight: 500 !important;
  font-size: 15px !important;
`

const StyledFieldValue = styled(Text)`
  font-weight: 400 !important;
  font-size: 15px !important;
  color: ${({ theme }) => theme.color.neutral.color1};
`

const StyledModal = styled(Modal)`
  min-width: 600px;

  .ant-modal-content {
    padding-top: 20px !important;
    padding-left: 24px !important;
    padding-right: 24px !important;
  }

  .ant-form-item-label > label {
    font-weight: 500 !important;
    font-size: 15px !important;
  }

  .ant-form-item-label > label span {
    font-size: 15px !important;
  }

  .ant-form-item-explain-error {
    font-size: 15px !important;
  }

  .ant-select {
    height: 40px !important;
  }

  .ant-modal-header {
    padding-bottom: 4px !important;
  }

  .ant-modal-title {
    font-size: 22px !important;
    font-weight: 500 !important;
    text-transform: none !important;
    color: ${({ theme }) => theme.color.neutral.color3} !important;
  }

  .ant-modal-footer {
    margin-top: 0 !important;
    padding-top: 16px !important;
    padding-bottom: 16px !important;
  }
`

export default EditNeedModal
